import {
  AfterViewChecked, Component, DoCheck, ElementRef, HostBinding, NgZone,
  HostListener, OnDestroy, OnInit, ViewChild,
} from '@angular/core';
import { ReasoningEffort } from '../../services/chat-execution';
import { ComposerModelOption, composerEffortPlaceholder } from './composer-model-options.component';
import { ConversationTaskView } from './conversation-tasks.component';
import { conversationTaskPreview } from './conversation-tasks.preview';
import { ConversationAgentOption, conversationAgentPlaceholders } from './conversation-agents.component';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import {
  openConfigurationDialog,
} from '../../configuration/configuration-dialog/configuration-dialog.component';
import { Subscription } from 'rxjs';
import { MapFeatureManagerService } from '../../map/core/map-feature-manager.service';
import {
  ChatKeyOption, ChatMessage,
  CommandConversationService,
  MapLocateAction,
  OverviewAnalysisPublished,
} from '../../services/command-conversation.service';
import { hasRoutedWorkspaceCard, SELECTION_WORKSPACE_ROUTE } from '../../services/workspace-route';
import { WorkspaceLayoutService } from '../../services/workspace-layout.service';
import { WorkspaceNavigationService } from '../../services/workspace-navigation.service';
import { overviewAnalysisContext } from '../../pages/home/overview-routing';
import {
  FeatureSelectionSummary,
  MapSelectionContextService,
} from '../../map/core/map-selection-context.service';
import {
  LeadTableListSort,
  LeadTableViewRule,
} from '../../services/current-surface-action.service';

const sampleEffort = { efforts: composerEffortPlaceholder, defaultEffort: 'high' as const };
const sampleModels: readonly ComposerModelOption[] = [
  { id: 'codex', connection: 'codex', label: 'GPT 6', model: 'gpt-6-astra', ...sampleEffort },
  { id: 'codex-gpt55', connection: 'codex', label: 'GPT 5.5', model: 'gpt-5.5', ...sampleEffort },
  { id: 'claude', connection: 'claude', label: 'Claude 5.1', ...sampleEffort },
  { id: 'api', connection: 'api', label: 'GPT 6', model: 'gpt-6-astra', ...sampleEffort },
  { id: 'api-gpt55', connection: 'api', label: 'GPT 5.5', model: 'gpt-5.5', ...sampleEffort },
  { id: 'api-claude51', connection: 'api', label: 'Claude 5.1', ...sampleEffort },
];

@Component({
  selector: 'app-command-bar',
  templateUrl: './command-bar.component.html',
  styleUrls: ['./command-bar.component.css', './command-bar.conversation.css', './conversation-scrollbar.css', './command-bar.tasks.css', './command-bar.agents.css'],
})
export class CommandBarComponent implements OnInit, OnDestroy, DoCheck, AfterViewChecked {
  get userInput(): string { return this.convo.draft; }
  set userInput(value: string) { this.convo.draft = value; }
  @ViewChild('ci') private composerInput?: ElementRef<HTMLTextAreaElement>;

  /** The floating chat surface height; Workspace composition stretches the host. */
  private readonly minPanelHeight = 200;
  panelHeight = Math.max(this.minPanelHeight, Math.min(440, Math.round(window.innerHeight * 0.55)));

  private dragging = false;
  private resized = false;
  private startY = 0;
  private startH = 0;

  @ViewChild('transcript') private transcript?: ElementRef<HTMLDivElement>;
  @ViewChild('transcriptContent') private transcriptContent?: ElementRef<HTMLDivElement>;
  // Infinity requests the latest content after layout; a finite offset preserves reading.
  private transcriptOffset = Infinity;
  private transcriptObserver?: ResizeObserver;
  private transcriptAnchor?: { key: string; offset: number };
  private readonly localMessageKeys = new WeakMap<ChatMessage, string>();
  private nextLocalMessageKey = 0;
  private lastTranscriptScrollTop = 0;
  private bottomRequestPending = false;
  showScrollToBottom = false;
  private lastTranscriptEl?: HTMLElement;

  /** Provider-picker popover open state (UI-only; the choice lives on the service). */
  providerMenuOpen = false;
  panelView: 'conversation' | 'tasks' | 'agents' = 'conversation';
  get tasksOpen(): boolean { return this.panelView === 'tasks'; }
  get agentsOpen(): boolean { return this.panelView === 'agents'; }
  get panelOpen(): boolean { return this.panelView !== 'conversation'; }
  readonly agents = conversationAgentPlaceholders;
  mentionOptions: readonly ConversationAgentOption[] = [];
  mentionIndex = 0;
  private mentionStart: number | null = null;
  private mentionEnd = 0;
  private cachedChatKeys: readonly ChatKeyOption[] | null = null;
  private cachedComposerModels: readonly ComposerModelOption[] = sampleModels;
  private selectedComposerModelId: string | null = null;
  private cachedSelectedModel: string | null = null;
  tasks: readonly ConversationTaskView[] = [];

  get unreadCompletedTaskCount(): number {
    return this.tasks.filter(task => task.status === 'completed' && task.unread).length;
  }

  markTaskRead(id: string): void {
    this.tasks = this.tasks.map(task => task.id === id && task.status === 'completed' && task.unread
      ? { ...task, unread: false } : task);
  }

  get hasPanelContent(): boolean { return this.panelOpen || this.convo.messages.length > 0; }

  toggleTasks(): void {
    if (!this.tasks.length) this.tasks = conversationTaskPreview();
    this.togglePanel('tasks');
  }

  toggleAgents(): void { this.togglePanel('agents'); }

  private togglePanel(view: 'tasks' | 'agents'): void {
    if (this.panelView === view && this.convo.panelOpen) { this.showConversation(); return; }
    this.captureTranscriptPosition();
    this.panelView = view;
    this.providerMenuOpen = false;
    this.closeMentions();
    this.convo.open();
  }

  showConversation(): void {
    if (!this.panelOpen) return;
    this.panelView = 'conversation';
  }

  // The cache replaces the key array on refresh; unchanged data keeps option identities stable.
  get composerModels(): readonly ComposerModelOption[] {
    const keys = this.convo.chatKeys;
    const selectedModel = this.convo.model ?? null;
    if (!keys.length && !selectedModel) return sampleModels;
    if (keys !== this.cachedChatKeys || selectedModel !== this.cachedSelectedModel) {
      this.cachedChatKeys = keys;
      this.cachedSelectedModel = selectedModel;
      this.cachedComposerModels = [
        ...Array.from(new Set([...keys.map(key => key.model?.trim()), ...(this.convo.provider === 'chat' ? [selectedModel] : [])].filter((model): model is string => !!model)))
          .filter(model => !sampleModels.some(option => option.connection === 'api' && option.model === model))
          .map(model => ({ id: 'api-model:' + model, connection: 'api' as const, label: model, model, ...sampleEffort })),
        ...sampleModels,
      ];
    }
    return this.cachedComposerModels;
  }
  private agentModelCatalog: readonly ComposerModelOption[] | null = null;
  private agentModelConnection: string | undefined;
  private cachedAgentModels: readonly ComposerModelOption[] = [];
  get agentModels(): readonly ComposerModelOption[] {
    const catalog = this.composerModels;
    const connection = this.composerModels.find(m => m.id === this.selectedModelId)?.connection;
    if (catalog !== this.agentModelCatalog || connection !== this.agentModelConnection) {
      this.agentModelCatalog = catalog;
      this.agentModelConnection = connection;
      this.cachedAgentModels = catalog.filter(m => m.connection === connection);
    }
    return this.cachedAgentModels;
  }
  get selectedModelId(): string {
    if (this.selectedComposerModelId) return this.selectedComposerModelId;
    const connection = this.convo.provider === 'codex' ? 'codex' : 'api';
    const model = this.convo.model ?? (connection === 'api' ? this.convo.chatKeys.find(key => key.id === this.convo.activeChatKeyId)?.model : null);
    return this.composerModels.find(option => option.connection === connection && option.model === model)?.id ?? connection;
  }
  get modelLabel(): string {
    const model = this.composerModels.find(item => item.id === this.selectedModelId);
    return model?.label || 'Default';
  }
  get selectedEffort(): ReasoningEffort | null {
    const model = this.composerModels.find(item => item.id === this.selectedModelId);
    if (!model?.efforts?.length) return null;
    return model.efforts.find(option => option.value === this.convo.effort)?.value
      ?? model.efforts.find(option => option.value === model.defaultEffort)?.value
      ?? model.efforts[0].value;
  }
  get effortLabel(): string | null {
    return this.composerModels.find(item => item.id === this.selectedModelId)?.efforts
      ?.find(option => option.value === this.selectedEffort)?.label ?? null;
  }
  selectEffort(level: ReasoningEffort): void { this.convo.effort = level; }
  pickKey(id: number | null): void {
    this.convo.model = this.composerModels.find(option => option.id === this.selectedModelId)?.model ?? null;
    this.convo.selectChatKey(id);
  }
  pickModel(model: ComposerModelOption): void {
    this.selectedComposerModelId = model.model ? null : model.id;
    // A UI-only choice has no execution path, so it also ends a call on the previous engine.
    if (!model.model) void this.convo.voice.stop();
    if (model.model) this.convo.model = model.model;
    if (model.connection === 'codex') this.convo.selectAgent();
    else if (model.connection === 'api') this.convo.provider = 'chat';
  }

  updateMentions(ta: HTMLTextAreaElement): void {
    if (ta.selectionStart !== ta.selectionEnd) { this.closeMentions(); return; }
    const match = /(?:^|\s)@([a-z]*)$/i.exec(ta.value.slice(0, ta.selectionStart));
    if (!match) { this.closeMentions(); return; }
    this.mentionStart = ta.selectionStart - match[1].length - 1;
    this.mentionEnd = ta.selectionStart + (/^[a-z]*/i.exec(ta.value.slice(ta.selectionStart))?.[0].length ?? 0);
    this.mentionOptions = this.agents.filter(agent => agent.name.toLowerCase().startsWith(match[1].toLowerCase()));
    this.mentionIndex = 0;
    this.providerMenuOpen = false;
  }

  onComposerKeyup(ev: KeyboardEvent, ta: HTMLTextAreaElement): void {
    if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(ev.key)) this.updateMentions(ta);
  }

  closeMentions(): void { this.mentionOptions = []; this.mentionStart = null; }

  insertMention(agent: ConversationAgentOption): void {
    const ta = this.composerInput?.nativeElement;
    if (!ta) return;
    const start = this.mentionStart ?? ta.selectionStart;
    const end = this.mentionStart === null ? ta.selectionEnd : this.mentionEnd;
    const before = this.userInput.slice(0, start);
    const after = this.userInput.slice(end);
    const token = (before && !/\s$/.test(before) ? ' ' : '') + '@' + agent.name + (/^\s/.test(after) ? '' : ' ');
    this.userInput = before + token + after;
    this.closeMentions();
    // Write immediately so caret placement does not race the next ngModel update.
    ta.value = this.userInput;
    ta.focus();
    ta.setSelectionRange(before.length + token.length, before.length + token.length);
    this.autogrow(ta);
  }

  onComposerKeydown(ev: KeyboardEvent, ta: HTMLTextAreaElement): void {
    if (ev.isComposing) return;
    if (this.mentionOptions.length) {
      if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
        ev.preventDefault();
        this.mentionIndex = (this.mentionIndex + (ev.key === 'ArrowDown' ? 1 : -1) + this.mentionOptions.length) % this.mentionOptions.length;
        return;
      }
      if (ev.key === 'Enter' && !ev.shiftKey) {
        ev.preventDefault(); this.insertMention(this.mentionOptions[this.mentionIndex]); return;
      }
      if (ev.key === 'Escape') { ev.preventDefault(); this.closeMentions(); return; }
    }
    if (ev.key === 'Enter') this.onEnter(ev, ta);
  }

  previewTaskAction(event: { id: string; action: 'cancel' | 'retry' }): void {
    this.tasks = this.tasks.map(task => task.id !== event.id ? task : {
      ...task, status: event.action === 'cancel' ? 'canceled' : 'queued',
      progress: event.action === 'cancel' ? 'Stopped' : 'Queued',
      error: undefined,
      subAgents: [],
    });
  }

  private readonly tableFieldLabels: Record<string, string> = {
    title: 'Title', stage: 'Stage', origin: 'Origin', salesRep: 'Sales rep',
    routeFit: 'Route fit', address: 'Address', businessTypes: 'Types',
    sourceCount: 'Sources', score: 'Score', contribution: 'Contribution',
    probability: 'Probability', nextActionDue: 'Next action',
    discoveredAt: 'Discovered', boardColumn: 'Stage',
    qualificationScore: 'Score', contributionBase: 'Contribution',
  };
  private readonly tableOperatorLabels: Record<string, string> = {
    eq: 'is', contains: 'contains', gte: 'at least', lte: 'at most',
    after: 'on or after', before: 'on or before',
  };

  /** Whether routed content is mounted in the shared Workspace shell. */
  get inWorkspace(): boolean {
    return hasRoutedWorkspaceCard(this.router.url);
  }

  /** Only windowed and maximized Workspaces compose chat in a right gutter.
   *  A right-docked Workspace leaves this shared surface floating over the map. */
  @HostBinding('class.workspace-assistant-gutter')
  get workspaceAssistantGutter(): boolean {
    return this.inWorkspace && !this.layout.dockedRight;
  }

  /** A docked Workspace leaves chat floating in the visible map area. */
  @HostBinding('class.workspace-docked-right')
  get workspaceDockedRight(): boolean {
    return this.inWorkspace && this.layout.dockedRight;
  }

  /** A maximized routed card is flush with the viewport's right edge instead
   *  of using the windowed card's --ws-right inset. Keep the composer on that
   *  same edge. */
  @HostBinding('class.workspace-maximized') get workspaceMaximized(): boolean {
    return this.workspaceAssistantGutter && this.layout.maximized;
  }



  selections: readonly FeatureSelectionSummary[] = [];
  private subs = new Subscription();

  constructor(
    private featureManager: MapFeatureManagerService,
    public convo: CommandConversationService,
    private host: ElementRef<HTMLElement>,
    private layout: WorkspaceLayoutService,
    private router: Router,
    public readonly selectionContext: MapSelectionContextService,
    private dialog: MatDialog,
    private readonly zone: NgZone,
    private readonly navigation: WorkspaceNavigationService,
  ) {}

  ngOnInit(): void {
    this.subs.add(this.selectionContext.selections$.subscribe(selections => {
      this.selections = selections;
    }));
    this.subs.add(this.convo.conversationSelected$.subscribe(() => {
      this.providerMenuOpen = false;
      this.panelView = 'conversation';
      this.selectedComposerModelId = null;
      this.closeMentions();
      this.tasks = [];
      this.transcriptAnchor = undefined;
      this.scrollToBottom();
      setTimeout(() => {
        const input = this.composerInput?.nativeElement;
        if (input) { this.autogrow(input); input.focus(); }
      });
    }));
    // Load available chat engines for the provider picker.
    this.convo.loadChatConfig();
  }

  ngOnDestroy(): void {
    this.transcriptObserver?.disconnect();
    this.subs.unsubscribe();
  }

  ngDoCheck(): void {
    // Read the old DOM before Angular adds a message, action or older history.
    this.captureTranscriptPosition();
  }

  ngAfterViewChecked(): void {
    this.renderTranscriptPosition();
  }

  transcriptMessageKey(message: ChatMessage): string {
    if (message.id) return 'saved:' + message.id;
    let key = this.localMessageKeys.get(message);
    if (!key) {
      key = 'local:' + this.nextLocalMessageKey++;
      this.localMessageKeys.set(message, key);
    }
    return key;
  }

  private captureTranscriptPosition(): void {
    const el = this.transcript?.nativeElement;
    if (!el || this.panelOpen || !el.clientHeight || el !== this.lastTranscriptEl || this.bottomRequestPending) return;
    // With browser anchoring disabled, layout alone can only clamp scrollTop.
    // Any other movement is a reading action, even before resize/scroll delivery.
    const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
    const expected = Math.min(this.lastTranscriptScrollTop, maxScroll);
    if (Math.abs(el.scrollTop - expected) < 0.5 && maxScroll - el.scrollTop > 2) return;
    const atBottom = maxScroll - el.scrollTop <= 2;
    this.transcriptOffset = atBottom ? Infinity : el.scrollTop;
    this.lastTranscriptScrollTop = el.scrollTop;
    this.showScrollToBottom = !atBottom;
    this.rememberTranscriptAnchor(el);
  }

  private rememberTranscriptAnchor(el: HTMLElement): void {
    const message = Array.from(el.querySelectorAll<HTMLElement>('.msg'))
      .find(node => node.offsetTop + node.offsetHeight > el.scrollTop);
    this.transcriptAnchor = message
      ? { key: message.dataset['messageKey']!, offset: message.offsetTop - el.scrollTop }
      : undefined;
  }

  private renderTranscriptPosition(): void {
    const el = this.transcript?.nativeElement;
    if (!el) {
      this.transcriptObserver?.disconnect();
      this.lastTranscriptEl = undefined;
      return;
    }
    if (this.panelOpen || !el.clientHeight) return;
    this.captureTranscriptPosition();
    if (el !== this.lastTranscriptEl) {
      this.transcriptObserver?.disconnect();
      // Observe viewport and natural content size independently of Angular checks.
      this.zone.runOutsideAngular(() => {
        this.transcriptObserver = new ResizeObserver(() => this.zone.run(() => {
          this.renderTranscriptPosition();
        }));
        this.transcriptObserver.observe(el);
        if (this.transcriptContent) this.transcriptObserver.observe(this.transcriptContent.nativeElement);
      });
    }

    const height = el.scrollHeight;
    if (this.transcriptOffset !== Infinity && this.transcriptAnchor) {
      // Stable message identity survives collapse and refreshed history objects.
      const anchor = Array.from(el.querySelectorAll<HTMLElement>('.msg'))
        .find(node => node.dataset['messageKey'] === this.transcriptAnchor!.key);
      if (anchor) this.transcriptOffset = anchor.offsetTop - this.transcriptAnchor.offset;
    }
    const target = this.transcriptOffset === Infinity ? height : this.transcriptOffset;
    if (el.scrollTop !== Math.min(target, Math.max(0, height - el.clientHeight))) el.scrollTop = target;
    if (this.transcriptOffset !== Infinity) this.transcriptOffset = el.scrollTop;
    this.bottomRequestPending = false;
    this.lastTranscriptEl = el;
    this.lastTranscriptScrollTop = el.scrollTop;
    this.rememberTranscriptAnchor(el);
  }

  onTranscriptScroll(): void {
    this.captureTranscriptPosition();
  }

  scrollToBottom(): void {
    this.transcriptOffset = Infinity;
    // Keep the explicit request until layout, without recreating the observer.
    this.bottomRequestPending = true;
    this.showScrollToBottom = false;
  }

  toggleConversation(event: MouseEvent): void {
    // A resize can finish with a native click; keyboard activation has detail 0.
    if (this.resized && event.detail > 0) {
      this.resized = false;
      return;
    }
    this.resized = false;
    if (this.convo.panelOpen) this.convo.close();
    else this.convo.open();
  }

  startResize(e: PointerEvent): void {
    this.resized = false;
    if (this.workspaceAssistantGutter || !this.convo.panelOpen || e.button !== 0) return;
    this.dragging = true;
    this.startY = e.clientY;
    this.startH = this.panelHeight;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  }

  onResize(e: PointerEvent): void {
    if (!this.dragging) return;
    const delta = this.startY - e.clientY;
    if (!this.resized && Math.abs(delta) < 4) return;
    this.resized = true;
    const cap = window.innerHeight - 80; // Match the card's CSS viewport inset.
    this.panelHeight = Math.max(this.minPanelHeight, Math.min(cap, this.startH + delta));
  }

  endResize(): void { this.dragging = false; }

  /** Open/close the engine picker. On open, refresh the chat-key list so a key just
   *  added / removed / re-defaulted in Services shows without a page reload. */
  toggleProviderMenu(): void {
    this.providerMenuOpen = !this.providerMenuOpen;
    this.closeMentions();
    if (this.providerMenuOpen) this.convo.loadChatConfig(true);
  }

  /** Open AI settings from a reply that needs account configuration. */
  openServices(): void {
    this.providerMenuOpen = false;
    openConfigurationDialog(this.dialog, 'apiKeys');
  }

  leadRuleSummary(rule: LeadTableViewRule): string {
    return `${this.tableFieldLabels[rule.field] ?? rule.field} `
      + `${this.tableOperatorLabels[rule.op] ?? rule.op} ${rule.value}`;
  }

  leadSortSummary(sort: LeadTableListSort): string {
    const field = this.tableFieldLabels[sort.field] ?? sort.field;
    return `${field}, ${sort.direction === 'desc' ? 'descending' : 'ascending'}`;
  }

  /** Dismiss outside menus; collapse floating chat only when the map is clicked. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(e: MouseEvent): void {
    const target = e.target as Node;
    const composer = this.host.nativeElement.querySelector('.composer');
    if (composer && !composer.contains(target)) this.closeMentions();
    if (this.providerMenuOpen) {
      const ctrl = this.host.nativeElement.querySelector('.provider-control');
      if (ctrl && !ctrl.contains(target)) this.providerMenuOpen = false;
    }
    // A map click folds the card down to its composer. Sidebar selection owns
    // opening the chosen conversation; workspace interactions keep chat visible.
    if (this.convo.panelOpen && !this.workspaceAssistantGutter) {
      // The clicked suggestion may already be removed; its original event path
      // still identifies the composer as the click's owner.
      const path = e.composedPath();
      if (!path.includes(this.host.nativeElement) && path.some(node => node instanceof Element && node.matches('app-map'))) {
        this.convo.close();
      }
    }
  }

  // ── Command input → conversation ──────────────────────────────────────────
  send(ta?: HTMLTextAreaElement): void {
    // Keyboard sends must honor the same busy/recovery guard as the button.
    const conversation = this.convo.activeConversation;
    if (this.convo.voice.state === 'connecting') return;
    if (this.convo.isWorking(conversation) || conversation.unconfirmedTurn) return;
    // UI-only selections have no execution path yet; keep the draft for review.
    const model = this.composerModels.find(item => item.id === this.selectedModelId);
    if (!model?.model) return;
    const text = this.userInput.trim();
    this.closeMentions();
    // Always tidy the field (clear + reset height), even if it held only
    // whitespace — otherwise blank whitespace lingers, hiding the placeholder
    // and leaving the box grown.
    this.userInput = '';
    if (ta) ta.style.height = 'auto';
    if (text) { this.showConversation(); this.convo.send(text, { model: model?.model, effort: this.selectedEffort ?? undefined }); }
  }

  get voiceActive(): boolean { return this.convo.voice.state === 'live' || this.convo.voice.state === 'connecting'; }

  /** Live voice is opened and closed only from the Composer Live voice button. */
  toggleVoice(): void {
    if (this.voiceActive) { void this.convo.voice.stop(); return; }
    // UI-only selections have no execution path yet, like typed sends.
    if (!this.composerModels.find(item => item.id === this.selectedModelId)?.model) return;
    this.showConversation();
    // Each delegated task resolves the conversation's current settings at dispatch.
    void this.convo.startVoice();
  }

  /** Drop a whitespace-only draft on blur so the placeholder comes back and the
   *  box shrinks. A real draft is left untouched. */
  onBlur(ta: HTMLTextAreaElement): void {
    this.closeMentions();
    if (!this.userInput.trim()) {
      this.userInput = '';
      ta.style.height = 'auto';
    }
  }

  /** Enter sends; Shift+Enter inserts a newline. */
  onEnter(e: Event, ta: HTMLTextAreaElement): void {
    const ev = e as KeyboardEvent;
    if (ev.shiftKey || ev.isComposing) return;
    ev.preventDefault();
    this.send(ta);
  }

  /** Grow the command input with its content, up to a cap (then it scrolls). */
  autogrow(ta: HTMLTextAreaElement): void {
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 140) + 'px';
  }

  toggleWorkspace(): void {
    if (this.inWorkspace) {
      void this.router.navigateByUrl('/');
    } else {
      this.openWorkspace();
    }
  }

  /** Open the live business-object selection in the shared Workspace. */
  openWorkspace(featureKey = this.selections[0]?.feature.key): void {
    void this.router.navigateByUrl(SELECTION_WORKSPACE_ROUTE
      + (featureKey ? `?feature=${encodeURIComponent(featureKey)}` : ''));
  }

  /** Reveal the persistent map and move it to the provider-resolved address.
   *  This is a view action only: it creates no marker or business record. */
  async locateOnMap(location: MapLocateAction): Promise<void> {
    if (hasRoutedWorkspaceCard(this.router.url)) {
      const navigated = await this.router.navigateByUrl('/');
      if (!navigated) return;
    }
    this.convo.open();
    await this.convo.locateOnMap(location);
  }

  /** Open a report this turn published; the routed detail reloads it from the server.
   *  From Overview, its list context rides along and Back returns to that exact list. */
  openOverviewAnalysis(published: OverviewAnalysisPublished): void {
    const url = this.router.parseUrl(this.router.url);
    const fromOverview = url.root.children['primary']?.segments[0]?.path === 'overview';
    void this.router.navigate(['/overview', 'reports', published.id], fromOverview
      ? { queryParams: overviewAnalysisContext(url.queryParamMap), state: this.navigation.sourceState('Business Insight') } : {});
  }

  // ── Existing tools ──────────────────────────────────────────────────────
  refresh(): void {
    this.featureManager.clearAll();
    this.featureManager.renderAll();
  }
}
